import { AuthStore } from 'auth/storage/auth-storage-manager';
import {
  AuthenticationMethod,
  CreateAfAuthMethodProps
} from 'auth/types/authentication-method.type';
import { generateCodeChallenge, generateCodeVerifier } from 'auth/auth-helpers/pkce-codes';
import { AuthenticationOptions } from 'auth/types/authentication-options.type';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { TokenResponse } from 'auth/types/token-response.type';
import { getAccess } from 'auth/auth-helpers/get-access';
import { POST_AUTH_AF_SSO_REDIRECT_KEY } from 'auth/auth-helpers/auth-helper';
import Qs from 'qs';
import { replaceWithDynamicHost } from 'utils/api/api-host-utils';
import { differenceInSeconds } from 'date-fns';
import { initLocalJwtToken } from 'auth/auth-helpers/local-jwt';

const isLocal = import.meta.env.MODE === 'development';

const logout =
  ({ setToken, setSignedInUser, env }: CreateAfAuthMethodProps) =>
  async (): Promise<void> => {
    const clearSignedInUser = () => {
      AuthStore.setAuthToken(undefined);
      AuthStore.setUserInfo(undefined);
      setToken(undefined);
      setSignedInUser(undefined);
    };
    const token = AuthStore.getAuthToken();
    if (!token) return;
    const authConfig = env.auth;
    const refreshToken = token.refresh_token;

    const revokeTokenRequest: AxiosRequestConfig = {
      url: authConfig.afSso.tokenRevoker,
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        apiKey: authConfig.afSso.clientId
      },
      data: Qs.stringify({
        client_id: authConfig.afSso.clientId,
        token_type_hint: 'refresh_token',
        token: refreshToken
      })
    };
    const requestInstance = axios.create(revokeTokenRequest);

    try {
      await requestInstance.request(revokeTokenRequest);
    } finally {
      clearSignedInUser();
    }
  };

const login =
  ({ setToken, setSignedInUser, env, navigate }: CreateAfAuthMethodProps) =>
  (authenticationOptions?: AuthenticationOptions): Promise<boolean> => {
    const token = AuthStore.getAuthToken();
    if (token) {
      const isTokenExpired = token.token_expiry_time < new Date();
      if (!isTokenExpired) {
        setToken(token);
        return Promise.resolve(true);
      }
    }

    const authSuccessRedirect = authenticationOptions?.authSuccessRedirect ?? '/';
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = generateCodeChallenge(codeVerifier);
    AuthStore.setCodeVerifier(codeVerifier);
    AuthStore.setUserInfo(undefined);
    AuthStore.setAuthToken(undefined);
    setToken(undefined);
    setSignedInUser(undefined);

    const redirectUri = `${window.location.origin}${env.auth.afSso.redirectUriPath}`;

    let authUrl = replaceWithDynamicHost(env.auth.afSso.provider, env.apiHostMappings);
    authUrl = authUrl.includes('?') ? authUrl.concat('&') : authUrl.concat('?');
    authUrl = authUrl.concat('response_type=code');
    authUrl = authUrl.concat('&code_challenge_method=S256');
    authUrl = authUrl.concat(`&code_challenge=${codeChallenge}`);
    authUrl = authUrl.concat(`&client_id=${env.auth.afSso.clientId}`);
    authUrl = authUrl.concat(`&redirect_uri=${redirectUri}`);
    authUrl = authUrl.concat(`&state=${encodeURIComponent(authSuccessRedirect)}`);

    const currentMapping = env.auth.afSso.ssoHostMappings.find(
      (mapping) => mapping.host === window.location.hostname
    );
    if (!currentMapping) {
      return Promise.reject('No authentication mapping found for current host');
    }

    authUrl = authUrl.concat(`&brand=${currentMapping.brand}`);
    if (currentMapping.realm && currentMapping.realm !== '') {
      authUrl = authUrl.concat(`&realm=${currentMapping.realm}`);
    }

    if (env.auth.afSso.scopes.length > 0) {
      const scopes = env.auth.afSso.scopes.join('%20');
      authUrl = authUrl.concat(`&scope=${scopes}`);
    }
    let pathURL = window.location.pathname;
    if (env.routerBasePath !== '/' && pathURL.includes(env.routerBasePath)) {
      pathURL = pathURL.substring(env.routerBasePath.length);
    }

    localStorage.setItem(POST_AUTH_AF_SSO_REDIRECT_KEY, pathURL);
    navigate(authUrl);
    return Promise.resolve(true);
  };

const getUserAccess =
  (props: CreateAfAuthMethodProps) =>
  async (token: TokenResponse | undefined): Promise<boolean> => {
    const getAccessSuccess = await getAccess({
      setSignedInUser: props.setSignedInUser,
      getSignedInUser: props.getSignedInUser,
      setCurrentAuthenticatedUserWithFeatures: props.setCurrentAuthenticatedUserWithFeatures,
      env: props.env,
      navigate: props.navigate
    })(token);

    if (getAccessSuccess) {
      return getAccessSuccess;
    }
    const logoutUser = logout(props);
    await logoutUser();

    if (!isLocal) {
      AuthStore.setAuthToken(undefined);
      AuthStore.setUserInfo(undefined);
      props.setToken(undefined);
      props.setSignedInUser(undefined);
      props.navigate(window.location.origin);
    }

    return false;
  };

const refreshSession = (props: CreateAfAuthMethodProps) => async (): Promise<boolean> => {
  const { setToken, env } = props;
  const currentToken = AuthStore.getAuthToken();
  const logoutUser = logout(props);
  if (!currentToken) {
    await logoutUser();
    return false;
  }
  const secondsUntilExpiry = differenceInSeconds(currentToken.token_expiry_time, new Date());
  const isTokenExpired = secondsUntilExpiry < env.auth.refreshIntervalSeconds;
  if (!isTokenExpired) return true;

  const onRefreshSuccess = async (refreshedToken: TokenResponse): Promise<boolean> => {
    const tokenExpiryTime = new Date();
    tokenExpiryTime.setSeconds(tokenExpiryTime.getSeconds() + refreshedToken.expires_in);
    refreshedToken.token_expiry_time = tokenExpiryTime;

    AuthStore.setAuthToken(refreshedToken);
    setToken(refreshedToken);

    const localJwtProvider = props.env.auth.afSso.jwtProvider;

    if (localJwtProvider) {
      const localJwt = await initLocalJwtToken({
        gatewayToken: refreshedToken,
        localJwtProvider: props.env.auth.afSso.jwtProvider,
        bffApiUrl: replaceWithDynamicHost(props.env.apiUrl, props.env.apiHostMappings)
      });
      if (!localJwt) return false;
    }

    return true;
  };

  const authConfig = env.auth;

  const tokenRefreshRequest: AxiosRequestConfig = {
    url: authConfig.afSso.tokenProvider,
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    data: Qs.stringify({
      grant_type: 'refresh_token',
      client_id: authConfig.afSso.clientId,
      refresh_token: currentToken.refresh_token
    })
  };
  const tokenRefreshInstance = axios.create(tokenRefreshRequest);
  let refreshedToken: TokenResponse | undefined = undefined;

  try {
    const tokenRefreshResponse: AxiosResponse<TokenResponse> =
      await tokenRefreshInstance.request(tokenRefreshRequest);
    if (tokenRefreshResponse.status !== 200) {
      await logoutUser();
      return false;
    }
    refreshedToken = tokenRefreshResponse.data;
  } catch (_error) {
    await logoutUser();
    return false;
  }

  if (!refreshedToken) {
    await logoutUser();
    return false;
  }

  return await onRefreshSuccess(refreshedToken);
};

const retrieveUserInfo =
  () =>
  async (accessToken?: string): Promise<boolean> => {
    const userInfoToken = accessToken ?? AuthStore.getAuthToken()?.access_token;
    if (!userInfoToken) return false;
    return true;
  };

export const createAfAuthMethod = (props: CreateAfAuthMethodProps): AuthenticationMethod => {
  return {
    logout: logout(props),
    login: login(props),
    refreshSessionIfExpired: refreshSession(props),
    retrieveUserInfo: retrieveUserInfo(),
    getAccess: getUserAccess(props),
    setToken: props.setToken,
    getSignedInUser: props.getSignedInUser,
    identificationState: props.identificationState
  };
};
